/**
 * Intent-aware auto-surfacing — opens contextual panels when the user's
 * actions strongly suggest they need them.
 *
 * Each trigger fires at most once per session to avoid repeated/annoying
 * auto-opens. The hook only OPENS panels, never closes them.
 */

import { useEffect, useRef } from "react";
import { getSingleSelectionTarget } from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import { storeKeyToEntityId, useEntityRegistry } from "../ecs/index.ts";
import { useFloatingPanelStore } from "./floatingPanelStore.ts";

export function useAutoSurface(): void {
  const fired = useRef(new Set<string>());
  const selectedTarget = useStore((s) =>
    getSingleSelectionTarget(s.selectedObjectTargets),
  );
  const workspaceMode = useStore((s) => s.workspaceMode);
  const registry = useEntityRegistry();
  const open = useFloatingPanelStore((s) => s.open);

  // Selection-based triggers
  useEffect(() => {
    if (!selectedTarget || selectedTarget.kind !== "sceneObject") return;
    const id = selectedTarget.id;
    const entity = registry.get(storeKeyToEntityId(id));
    if (!entity) return;

    if (entity.kind === "light" && !fired.current.has("light")) {
      fired.current.add("light");
      open("inspector");
    }
    if (entity.kind === "mannequin" && !fired.current.has("mannequin")) {
      fired.current.add("mannequin");
      open("shapekeys");
    }
  }, [selectedTarget, registry, open]);

  // Mode-based triggers
  useEffect(() => {
    if (workspaceMode === "sketch" && !fired.current.has("sketch")) {
      fired.current.add("sketch");
      open("sketch");
    }
    if (workspaceMode === "reference" && !fired.current.has("reference")) {
      fired.current.add("reference");
      open("reference");
    }
  }, [workspaceMode, open]);
}
